import cn from "@utils/cn";
import { DEFAULT_CURRENCY } from "@utils/default";
import { inputBlur, inputChange } from "@utils/rangeSliderEvents";
import { useFormatter } from "next-intl";
import Image from "next/image";
import { ChangeEvent, FC, useRef, useState } from "react";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";

import { GuidedToolSlideProps } from "@/types";

import { useGuidedToolContext } from "./GuidedToolProvider";

const RangeSelector: FC<GuidedToolSlideProps> = ({ slide, isPriceRange }) => {
  const { setCurrentAnswer, setCurrentFilter } = useGuidedToolContext();
  const format = useFormatter();

  const [sliderValues, setSliderValues] = useState([slide.rangeMin!, slide.allowRangeSelection ? slide.rangeMax! : slide.rangeMin!]);

  const icons = () => {
    var icons = [];
    if (slide.icon && sliderValues[1]) {
      for (var i = 0; i < sliderValues[1]; i++) {
        icons.push(<Image key={i} className="inline-flex px-3xs" src={slide.icon.url} alt={slide.icon.title || ""} width={50} height={90} />);
      }
    }
    return icons;
  };

  const handleInputChange = (value: string, isMin: boolean) => {
    const inputValue = Number(value);
    const newValues = inputChange(inputValue, isMin, sliderValues);
    setSliderValues(newValues);
    if (isPriceRange) {
      setCurrentFilter({ minimumUnitPrice: newValues[0], maximumUnitPrice: newValues[1] });
    } else {
      setCurrentAnswer(newValues);
    }
  };

  const handleBlur = (value: string, isMin: boolean) => {
    const inputValue = Number(value);
    const newValues = inputBlur(inputValue, isMin, sliderValues, slide.rangeMin!, slide.rangeMax!);
    setSliderValues(newValues);
    if (isPriceRange) {
      setCurrentFilter({ minimumUnitPrice: newValues[0], maximumUnitPrice: newValues[1] });
    } else {
      setCurrentAnswer(newValues);
    }
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1");
  };

  return (
    <div className="py-4xl md:py-3xl">
      {slide.allowRangeSelection ? (
        <div className="flex justify-between  pb-l">
          <div>
            <div className="text-s font-primary font-normal md:font-medium uppercase pb-xs">from</div>
            <div className=" font-medium text-s md:text-l border-b font-primary">
              {isPriceRange ? (
                <div className="py-xxs">
                  {format.number(Math.floor(sliderValues[0] / 100), {
                    style: "currency",
                    currency: DEFAULT_CURRENCY,
                    trailingZeroDisplay: "stripIfInteger",
                  })}
                </div>
              ) : (
                <input
                  type="text"
                  value={sliderValues[0]}
                  onChange={(event) => handleInputChange(event.target.value, true)}
                  onBlur={(event) => handleBlur(event.target.value, true)}
                  className="py-xxs min-w-m bg-transparent focus:outline-none focus-visible:ring"
                  onInput={handleInput}
                  style={{
                    width: `${Math.max(1, sliderValues[0].toString().length)}ch`,
                  }}
                />
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-s font-primary font-medium uppercase pb-xs">to</div>
            <div className="font-medium text-s md:text-l border-b border-b-white font-primary">
              {isPriceRange ? (
                <div className="py-xxs">
                  {format.number(Math.floor(sliderValues[1] / 100), {
                    style: "currency",
                    currency: DEFAULT_CURRENCY,
                    trailingZeroDisplay: "stripIfInteger",
                  })}
                </div>
              ) : (
                <input
                  type="text"
                  step="10"
                  value={sliderValues[1]}
                  onChange={(event) => handleInputChange(event.target.value, false)}
                  onBlur={(event) => handleBlur(event.target.value, false)}
                  className="py-xxs min-w-m  bg-transparent focus:outline-none focus-visible:ring"
                  onInput={handleInput}
                  style={{
                    width: `${Math.max(1, sliderValues[0].toString().length)}ch`,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="pb-l text-center">
          {slide.icon && <div className="text-center">{icons()}</div>}
          <div className="font-primary font-medium text-xl pt-xl">{sliderValues[1]}</div>
        </div>
      )}
      <RangeSlider
        id={slide.id}
        className={cn("guided-tool-range-slide  my-l", !slide.allowRangeSelection && "single-slider")}
        min={slide.rangeMin}
        max={slide.rangeMax}
        {...(!slide.allowRangeSelection
          ? {
              rangeSlideDisabled: true,
              thumbsDisabled: [true, false],
              defaultValue: [slide.rangeMin || 1, slide.rangeMin || 1],
            }
          : {
              value: [sliderValues[0], sliderValues[1]],
            })}
        onInput={(values) => {
          if (slide.allowRangeSelection) {
            let minValue = Math.min.apply(Math, values);
            let maxValue = Math.max.apply(Math, values);
            setSliderValues([minValue, maxValue]);
            if (isPriceRange) {
              setCurrentFilter({ minimumUnitPrice: minValue, maximumUnitPrice: maxValue });
            } else {
              setCurrentAnswer([minValue, maxValue]);
            }
          } else {
            setSliderValues(values);
            if (isPriceRange) {
              setCurrentFilter({ minimumUnitPrice: values[1] });
            } else {
              setCurrentAnswer(values[1]);
            }
          }
        }}
      />
    </div>
  );
};
export default RangeSelector;
